import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import BaseTabbedTemplate from "new_components/templates/BaseTabbedTemplate";
import { useNavigate } from "react-router-dom";
import {
  useSurveyDataContext,
  SurveyDataProvider,
} from "./Data/SurveyViewContext";

// Components
import Text from "new_components/atoms/Text";
import colors from "styles/colors";
import typography from "styles/typography";
import TextInput from "new_components/molecules/TextInput";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";

// View Components
import SummaryView from "./ViewComponents/SummaryView";
import QuestionsView from "./ViewComponents/QuestionsView";
import IndividualView from "./ViewComponents/IndividualView";
import EmployeeAccessPage from "./ViewComponents/EmployeeAccess";
import DeleteModal from "new_components/organisms/DeleteModal";
import Button from "new_components/atoms/Button";
import Filters from "./ViewComponents/Filters";

const InfoCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 25px;
  border-radius: 8px;
  width: 100%;
  background-color: ${colors.background.lightBlue};
`;

const InfoTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-direction: column;
`;

const InfoBottomRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 35px;
  flex-direction: row;
`;

const ComplianceRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 20px;
  flex-direction: row;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 50%;
`;

const RightSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 50%;
  justify-content: flex-end;
`;

const ComplianceSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;
  justify-content: flex-end;
`;

const ComplianceContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-end;
  gap: 40px;
`;

const RightContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 80%;
  justify-content: space-between;
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EditableField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding: 4px 0px;
  border-radius: 4px;
  transition: background-color 0.2s;
  width: 100%;
  &:hover {
    background-color: ${colors.background.lightGray};
  }
`;

const EditIconWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;

  ${EditableField}:hover & {
    opacity: 1;
  }
`;

const SaveIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-left: 8px;
`;

const TopRightSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
  max-height: 40px;
`;

const ChatIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ChatButton = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  height: 50px;
  width: 50px;
  border-radius: 25px;
  background: ${colors.secondary[500]};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 2px solid white;
  transition: all 0.3s ease;
  z-index: 9999;
  gap: 0;
  overflow: hidden;
  white-space: nowrap;

  &:hover {
    width: 200px;
    padding: 0 20px;
    transform: scale(1.05);
    gap: 8px;
  }
`;

const ButtonText = styled.span`
  color: white;
  font-family: Raleway;
  font-size: 14px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s ease;
  transition-delay: 0.1s;
  width: 0;

  ${ChatButton}:hover & {
    opacity: 1;
    width: auto;
  }
`;

const SVGIMG = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  margin: 0;

  ${ChatButton}:hover & {
    margin-right: 8px;
  }
`;

const InfoCard = ({
  title,
  description,
  createdBy,
  createdDate,
  updatedDate,
  surveyIsAnonymous,
  onUpdateName,
  task_template_id,
  setIsArchiveModalOpen,
  setIsDeleteModalOpen,
  surveyIsOpen,
  surveyId,
  compliance,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [tempName, setTempName] = useState(title);
  const [tempDescription, setTempDescription] = useState(description);
  const navigate = useNavigate();

  useEffect(() => {
    setTempName(title);
    setTempDescription(description);
  }, [title, description]);

  const handleNameSave = () => {
    onUpdateName(tempName, task_template_id);
    setIsEditingName(false);
  };

  const handleChatClick = () => {
    // Navigate to the copilot page with the survey ID
    navigate(`/leader/audit/AI/copilot?pulseId=${surveyId}`);
  };

  return (
    <InfoCardContainer>
      <TopSection>
        <InfoTopRow>
          <Heading>
            <Text
              typographyStyle={typography.smallText.semibold}
              color={colors.primary[500]}
            >
              Name
            </Text>
          </Heading>
          <EditableField>
            {isEditingName ? (
              <>
                <TextInput
                  value={tempName}
                  onChange={(value) => setTempName(value)}
                  placeholder="Enter name"
                  autoFocus
                />
                <SaveIconWrapper onClick={handleNameSave}>
                  <Icon name={ICON_SET.checkMark} size={20} color={colors.primary[500]} style={{ cursor: "pointer" }} />
                </SaveIconWrapper>
              </>
            ) : (
              <>
                  <Text
                    typographyStyle={typography.subhead.semibold}
                    color={colors.text.dark}
                  >
                    {title}
                  </Text>
                <EditIconWrapper onClick={() => setIsEditingName(true)}>
                  <Icon name={ICON_SET.pencil} size={20} color={colors.text.dark} style={{ cursor: "pointer" }} />
                </EditIconWrapper>
              </>
            )}
          </EditableField>
        </InfoTopRow>
        <TopRightSection>
          <Button style={{ backgroundColor: colors.primary[100], color: colors.primary[500] }}
            onClick={handleChatClick}>
            <div style={{ display: "flex", marginRight: 8, alignItems: "center", gap: 8 }}>
              <SVGIMG>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                  viewBox="0 0 256 256"
                >
                  <path
                    fill={colors.primary[600]}
                    d="M216 52H40a12 12 0 0 0-12 12v160a11.89 11.89 0 0 0 6.93 10.88A12.17 12.17 0 0 0 40 236a11.89 11.89 0 0 0 7.69-2.83l.06-.06l32.14-28.17a4 4 0 0 1 2.61-.94H216a12 12 0 0 0 12-12V64a12 12 0 0 0-12-12Zm4 140a4 4 0 0 1-4 4H82.5a12.1 12.1 0 0 0-7.79 2.87l-32.16 28.2A4 4 0 0 1 36 224V64a4 4 0 0 1 4-4h176a4 4 0 0 1 4 4Z"
                  />
                </svg>
              </SVGIMG>
            </div>
            Chat with this data
          </Button>
          {surveyIsOpen ? (
            <>
              <Button onClick={() => setIsDeleteModalOpen(true)}>Close Survey</Button>
            </>
          ) : (
              <>
                <Button onClick={() => setIsArchiveModalOpen(true)}>Archive Survey</Button>
              </>
          )}
        </TopRightSection>
      </TopSection>

      <InfoBottomRow>
        <LeftSection>
          <Text
            typographyStyle={typography.smallText.semibold}
            color={colors.primary[500]}
          >
            Description
          </Text>
          <Text
            typographyStyle={typography.smallText.regular}
            color={colors.text.gray}
          >
            {description}
          </Text>
          <Text
            typographyStyle={typography.smallText.regular}
            color={colors.text.gray}
          >
            This survey {surveyIsAnonymous ? "is" : "is not"}{" "}
            <strong>anonymous</strong>
          </Text>
        </LeftSection>
        <RightSection>
          <RightContent>
            <Heading>
              <Text
                typographyStyle={typography.smallText.semibold}
                color={colors.primary[500]}
              >
                Created By
              </Text>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                {createdBy}
              </Text>
            </Heading>
            <Heading>
              <Text
                typographyStyle={typography.smallText.semibold}
                color={colors.primary[500]}
              >
                Created Date
              </Text>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                {createdDate}
              </Text>
            </Heading>
            <Heading>
              <Text
                typographyStyle={typography.smallText.semibold}
                color={colors.primary[500]}
              >
                Updated Date
              </Text>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                {updatedDate}
              </Text>
            </Heading>
          </RightContent>
        </RightSection>
      </InfoBottomRow>

      <ComplianceRow>
        <ComplianceSection>
          <ComplianceContent>
            <Heading>
              <Text
                typographyStyle={typography.smallText.semibold}
                color={colors.primary[500]}
              >
                Response Rate
              </Text>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                {compliance?.percentage || 0}%
              </Text>
            </Heading>
            <Heading>
              <Text
                typographyStyle={typography.smallText.semibold}
                color={colors.primary[500]}
              >
                Responses
              </Text>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                {compliance?.respondedCount || 0} of {compliance?.totalParticipants || 0}
              </Text>
            </Heading>
          </ComplianceContent>
        </ComplianceSection>
      </ComplianceRow>
    </InfoCardContainer>
  );
};

InfoCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  createdBy: PropTypes.string,
  createdDate: PropTypes.string,
  updatedDate: PropTypes.string,
  surveyIsAnonymous: PropTypes.bool,
  onUpdateName: PropTypes.func,
  onUpdateDescription: PropTypes.func,
};

const PulseDataViewContent = ({ surveyId }) => {
  const navigate = useNavigate();
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const {
    surveyInfo,
    fetchSurvey,
    surveyParticipants,
    responses,
    questions,
    downloadableReports,
    surveyIsAnonymous,
    deleteEmployeeFromSurvey,
    addEmployeeToSurvey,
    handleEmailChange,
    saveChangesAndAssignUsers,
    handleEmailUpdate,
    addEmployeeReportAccess,
    removeEmployeeReportAccess,
    handleCloseSurvey,
    updateSurveyName,
    updateSurveyDescription,
    handleArchiveSurvey,
    compliance,
  } = useSurveyDataContext();

  const handleChatClick = () => {
    // Navigate to the copilot page with the survey ID
    navigate(`/leader/audit/AI/copilot?pulseId=${surveyId}`);
  };

  useEffect(() => {
    if (surveyId) {
      fetchSurvey(surveyId);
    }
  }, []);


  const getTabs = (isAnon) => {
    if (isAnon) {
      return [
        {
          label: "Summary",
          content: (
            <SummaryView
              removeEmployee={deleteEmployeeFromSurvey}
              assignEmployees={saveChangesAndAssignUsers}
              assignees={surveyParticipants}
              id={surveyId}
              handleEmailChange={handleEmailUpdate}
              isAnonymous={isAnon}
            />
          ),
        },
        {
          label: "Responses",
          content: (
            <QuestionsView
              participants={surveyParticipants}
              responses={responses}
              questions={questions}
              isAnonymous={isAnon}
              handleUserSelect={(userId) => setSelectedParticipant(userId)}
            />
          ),
        },
        {
          label: "Access",
          content: (
            <EmployeeAccessPage
              removeEmployee={removeEmployeeReportAccess}
              addAccess={addEmployeeReportAccess}
            />
          ),
        },
      ];
    }

    return [
      {
        label: "Summary",
        content: (
          <SummaryView
            removeEmployee={deleteEmployeeFromSurvey}
            assignEmployees={saveChangesAndAssignUsers}
            assignees={surveyParticipants}
            id={surveyId}
            handleEmailChange={handleEmailUpdate}
            isAnonymous={isAnon}
          />
        ),
      },
      {
        label: "Responses",
        content: (
          <QuestionsView
            participants={surveyParticipants}
            responses={responses}
            questions={questions}
            filters={<Filters />}
            handleUserSelect={(userId) => setSelectedParticipant(userId)}
          />
        ),
      },
      {
        label: "Individual",
        content: (
          <IndividualView
            participants={surveyParticipants}
            responses={responses}
            questions={questions}
            filters={<Filters />}
            selectedParticipant={selectedParticipant}
            clearUser={() => setSelectedParticipant(null)}
          />
        ),
      },
      {
        label: "Access",
        content: (
          <EmployeeAccessPage
            removeEmployee={removeEmployeeReportAccess}
            addAccess={addEmployeeReportAccess}
          />
        ),
      },
    ];
  };

  return (
    <>
      <BaseTabbedTemplate
        heading=""
        filters={<Filters />}
        pageTitle="Pulse AI Surveys"
        tabTrigger={selectedParticipant && 2}
        belowTabsSection={
          <InfoCard
            {...surveyInfo}
            surveyId={surveyId}
            onUpdateName={updateSurveyName}
            onUpdateDescription={updateSurveyDescription}
            setIsArchiveModalOpen={setIsArchiveModalOpen}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            onChatClick={handleChatClick}
            compliance={compliance}
          />
        }
        tabs={getTabs(surveyIsAnonymous)}
        backAction={() => {
          navigate("/leader/audit/AI/pulse");
        }}
        modal={
          surveyInfo.surveyIsOpen ? (
            <DeleteModal
              confirmTextOverride="close"
              headingOverride="Close Survey"
              isOpen={isDeleteModalOpen}
              onClose={() => setIsDeleteModalOpen(false)}
              onConfirm={() => {
                handleCloseSurvey(surveyId);
                setIsDeleteModalOpen(false);
              }}
              textOverride={
                "Are you sure you want to <strong>close</strong> this survey? <br /> This action cannot be undone."
              }
            />
          ) : (
            <DeleteModal
              confirmTextOverride="archive"
              headingOverride="Archive Survey"
              isOpen={isArchiveModalOpen}
              onClose={() => setIsArchiveModalOpen(false)}
              onConfirm={() => {
                handleArchiveSurvey(surveyInfo?.task_template_id);
                setIsArchiveModalOpen(false);
                navigate("/leader/audit/AI/pulse");
              }}
              textOverride={
                "Are you sure you want to <strong>archive</strong> this survey? <br /> This action cannot be undone."
              }
            />
          )
        }
      />
      {/* <ChatButton onClick={handleChatClick}>
        <SVGIMG>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="100%"
            height="100%"
            viewBox="0 0 256 256"
          >
            <path
              fill="white"
              d="M216 52H40a12 12 0 0 0-12 12v160a11.89 11.89 0 0 0 6.93 10.88A12.17 12.17 0 0 0 40 236a11.89 11.89 0 0 0 7.69-2.83l.06-.06l32.14-28.17a4 4 0 0 1 2.61-.94H216a12 12 0 0 0 12-12V64a12 12 0 0 0-12-12Zm4 140a4 4 0 0 1-4 4H82.5a12.1 12.1 0 0 0-7.79 2.87l-32.16 28.2A4 4 0 0 1 36 224V64a4 4 0 0 1 4-4h176a4 4 0 0 1 4 4Z"
            />
          </svg>
        </SVGIMG>
        <ButtonText>Chat with this data</ButtonText>
      </ChatButton> */}
    </>
  );
};

const PulseDataViewPage = ({ surveyId }) => {
  return (
    <SurveyDataProvider>
      <PulseDataViewContent surveyId={surveyId} />
      <div style={{ height: "100px" }}></div>
    </SurveyDataProvider>
  );
};

PulseDataViewPage.propTypes = {
  surveyId: PropTypes.string.isRequired,
};

export default PulseDataViewPage;
